import { validateFlow } from './util';
import { Flow, FlowLike, FlowNode } from './types/flow';
import { NonUniqueNamespaceError } from './errors';

export type DataContainer = Pick<
  FlowLike,
  'flowProps' | 'pageDecorators' | 'onCommit' | 'onLoad' | 'onApplicationLoad'
>;

export interface RegistrationContainer {
  nodeToNamespace: Map<FlowNode, string>;
  flowsRegistry: Set<string>;
  flowRoutes: Map<string, FlowNode>;
  namespaceToDataContainer: Map<string, DataContainer>;
}

const registeredFlows: FlowLike[] = [];

const register = (reg: RegistrationContainer, flowLiteral: FlowLike) => {
  const {
    nodeToNamespace,
    flowsRegistry,
    flowRoutes,
    namespaceToDataContainer
  } = reg;
  validateFlow(flowLiteral);

  const flow = new Flow({
    nodes: flowLiteral.nodes,
    namespace: flowLiteral.namespace,
    start: flowLiteral.start,
    flowProps: flowLiteral.flowProps,
    pageDecorators: flowLiteral.pageDecorators,
    onCommit: flowLiteral.onCommit,
    onLoad: flowLiteral.onLoad,
    onApplicationLoad: flowLiteral.onApplicationLoad
  });

  namespaceToDataContainer.set(flow.namespace, {
    flowProps: flow.flowProps,
    pageDecorators: flow.pageDecorators,
    onCommit: flow.onCommit,
    onLoad: flow.onLoad,
    onApplicationLoad: flow.onApplicationLoad
  });

  flow.nodes.forEach(n => nodeToNamespace.set(n, flow.namespace));

  const { namespace } = flow;

  const conflictingNamespace = Array.from(flowsRegistry).find(
    alreadyRegistered =>
      alreadyRegistered.indexOf(namespace) === 0 ||
      namespace.indexOf(alreadyRegistered) === 0
  );

  if (conflictingNamespace)
    throw new NonUniqueNamespaceError(`
    there is some confusion when registering namespaces ${namespace} and ${conflictingNamespace}.
    make sure one namespace doesn't contain the other`);

  flowsRegistry.add(flow.namespace);
  flowRoutes.set(flowLiteral.namespace, flow.getStartNode());
  flow.nodes.forEach(n => {
    return flowRoutes.set(`${flow.namespace}/${n.route}`, n);
  });
};

export const flowsToRegistrationContainer = (
  flows: FlowLike[]
): RegistrationContainer => {
  const registrationContainer: RegistrationContainer = {
    nodeToNamespace: new Map(),
    flowsRegistry: new Set(),
    flowRoutes: new Map(),
    namespaceToDataContainer: new Map()
  };

  flows.forEach(flow => register(registrationContainer, flow));
  return registrationContainer;
};

export const registerFlow = (flowLiteral: FlowLike) =>
  registeredFlows.push(flowLiteral);

export const getFlows = () => registeredFlows;
